// src/stores/analyticsStore.js
import { create } from 'zustand';
import * as analyticsService from '../services/analyticsService';
import useNotificationStore from './notificationStore';

// Helper function to format date for API
const formatDateForApi = (date) => {
  if (!date) return '';
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return dateObj.toISOString().split('T')[0];
};

// Create analytics store with Zustand
const useAnalyticsStore = create((set, get) => ({
  // Dashboard analytics
  dashboardData: null,
  // Campaign analytics
  campaignAnalytics: null,
  // Content analytics
  contentAnalytics: null,
  // Client analytics
  clientAnalytics: null,
  // Brand analytics
  brandAnalytics: null,
  // Social media analytics
  socialMediaAnalytics: null,
  // Custom report data
  customReportData: null,
  // Audience analytics
  audienceAnalytics: null,
  // Financial analytics
  financialAnalytics: null,
  // User activity analytics
  userActivityAnalytics: null,
  // Competitive analysis
  competitiveAnalytics: null,
  // Regional analytics
  regionalAnalytics: null,
  // Time tracking analytics
  timeTrackingAnalytics: null,
  // Loading states
  isLoading: false,
  // Export loading state
  isExporting: false,
  // Error state
  error: null,
  // Date range filters
  dateRange: {
    startDate: new Date(new Date().setMonth(new Date().getMonth() - 1)), // Default: 1 month ago
    endDate: new Date(),
  },

  // Reset error state
  clearError: () => set({ error: null }),

  // Format params for API calls
  prepareApiParams: (params = {}) => {
    const { dateRange } = get();
    return {
      startDate: params.startDate || formatDateForApi(dateRange.startDate),
      endDate: params.endDate || formatDateForApi(dateRange.endDate),
      ...params,
    };
  },

  // Fetch dashboard analytics
  fetchDashboardAnalytics: async (params = {}) => {
    set({ isLoading: true, error: null });
    try {
      const apiParams = get().prepareApiParams(params);
      console.log('Fetching dashboard analytics with params:', apiParams);
      
      const response = await analyticsService.getDashboardAnalytics(apiParams);
      set({
        dashboardData: response.data,
        isLoading: false,
      });
      return response.data;
    } catch (error) {
      console.error('Dashboard analytics error:', error);
      useNotificationStore.getState().showError(
        error.message || 'Failed to fetch dashboard analytics'
      );
      set({
        dashboardData: null,
        error: error.message,
        isLoading: false,
      });
      return null;
    }
  },

  // Fetch campaign analytics
  fetchCampaignAnalytics: async (campaignId, params = {}) => {
    set({ isLoading: true, error: null });
    try {
      const apiParams = get().prepareApiParams(params);
      console.log('Fetching campaign analytics with params:', apiParams);
      
      const response = await analyticsService.getCampaignAnalytics(campaignId, apiParams);
      set({
        campaignAnalytics: response.data,
        isLoading: false,
      });
      return response.data;
    } catch (error) {
      set({
        error: error.message || 'Failed to fetch campaign analytics',
        isLoading: false,
      });
      console.error('Campaign analytics error:', error);
      useNotificationStore.getState().showError(
        error.message || 'Failed to fetch campaign analytics'
      );
      return null;
    }
  },

  // Fetch content analytics
  fetchContentAnalytics: async (params = {}) => {
    set({ isLoading: true, error: null });
    try {
      const apiParams = get().prepareApiParams(params);
      console.log('Fetching content analytics with params:', apiParams);
      
      const response = await analyticsService.getContentAnalytics(apiParams);
      set({
        contentAnalytics: response.data,
        isLoading: false,
      });
      return response.data;
    } catch (error) {
      set({
        error: error.message || 'Failed to fetch content analytics',
        isLoading: false,
      });
      console.error('Content analytics error:', error);
      useNotificationStore.getState().showError(
        error.message || 'Failed to fetch content analytics'
      );
      return null;
    }
  },

  // Fetch client analytics
  fetchClientAnalytics: async (clientId, params = {}) => {
    if (!clientId) {
      set({ error: 'Client ID is required', isLoading: false });
      return null;
    }
    
    set({ isLoading: true, error: null });
    try {
      const apiParams = get().prepareApiParams(params);
      console.log('Fetching client analytics with params:', apiParams);
      
      const response = await analyticsService.getClientAnalytics(clientId, apiParams);
      set({
        clientAnalytics: response.data,
        isLoading: false,
      });
      return response.data;
    } catch (error) {
      set({
        error: error.message || 'Failed to fetch client analytics',
        isLoading: false,
      });
      console.error('Client analytics error:', error);
      useNotificationStore.getState().showError(
        error.message || 'Failed to fetch client analytics'
      );
      return null;
    }
  },

  // Fetch brand analytics
  fetchBrandAnalytics: async (brandId, params = {}) => {
    if (!brandId) {
      set({ error: 'Brand ID is required', isLoading: false });
      return null;
    }
    
    set({ isLoading: true, error: null });
    try {
      const apiParams = get().prepareApiParams(params);
      console.log('Fetching brand analytics with params:', apiParams);
      
      const response = await analyticsService.getBrandAnalytics(brandId, apiParams);
      set({
        brandAnalytics: response.data,
        isLoading: false,
      });
      return response.data;
    } catch (error) {
      set({
        error: error.message || 'Failed to fetch brand analytics',
        isLoading: false,
      });
      console.error('Brand analytics error:', error);
      useNotificationStore.getState().showError(
        error.message || 'Failed to fetch brand analytics'
      );
      return null;
    }
  },

  // Fetch social media analytics
  fetchSocialMediaAnalytics: async (params = {}) => {
    set({ isLoading: true, error: null });
    try {
      const apiParams = get().prepareApiParams(params);
      console.log('Fetching social media analytics with params:', apiParams);
      
      const response = await analyticsService.getSocialMediaPerformance(apiParams);
      set({
        socialMediaAnalytics: response.data,
        isLoading: false,
      });
      return response.data;
    } catch (error) {
      set({
        error: error.message || 'Failed to fetch social media analytics',
        isLoading: false,
      });
      console.error('Social media analytics error:', error);
      useNotificationStore.getState().showError(
        error.message || 'Failed to fetch social media analytics'
      );
      return null;
    }
  },

  // Generate and fetch custom report
  fetchCustomReport: async (reportConfig) => {
    set({ isLoading: true, error: null });
    try {
      console.log('Generating custom report with config:', reportConfig);
      
      const response = await analyticsService.getCustomReport(reportConfig);
      set({
        customReportData: response.data,
        isLoading: false,
      });
      return response.data;
    } catch (error) {
      set({
        error: error.message || 'Failed to generate custom report',
        isLoading: false,
      });
      console.error('Custom report error:', error);
      useNotificationStore.getState().showError(
        error.message || 'Failed to generate custom report'
      );
      return null;
    }
  },

  // Export analytics data
  exportAnalyticsData: async (params = {}) => {
    set({ isExporting: true, error: null });
    try {
      const apiParams = get().prepareApiParams(params);
      console.log('Exporting analytics data with params:', apiParams);
      
      const response = await analyticsService.exportAnalyticsData(apiParams);
      set({ isExporting: false });
      
      // Handle the blob response and trigger download
      if (response.data) {
        const url = window.URL.createObjectURL(response.data);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `analytics-export-${new Date().toISOString().split('T')[0]}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
      
      return true;
    } catch (error) {
      set({
        error: error.message || 'Failed to export analytics data',
        isExporting: false,
      });
      console.error('Export analytics error:', error);
      useNotificationStore.getState().showError(
        error.message || 'Failed to export analytics data'
      );
      return false;
    }
  },

  // Update date range
  setDateRange: (startDate, endDate) => {
    set({
      dateRange: {
        startDate: new Date(startDate),
        endDate: new Date(endDate),
      },
    });
  },
  
  // Fetch all dashboard data in one call
  fetchAllDashboardData: async (params = {}) => {
    set({ isLoading: true, error: null });
    try {
      const apiParams = get().prepareApiParams(params);
      console.log('Fetching all dashboard data with params:', apiParams);
      
      // Fetch dashboard overview data
      const dashboardData = await analyticsService.getDashboardAnalytics(apiParams);
      
      // Fetch content analytics
      const contentData = await analyticsService.getContentAnalytics(apiParams);
      
      // Fetch engagement metrics
      const engagementData = await analyticsService.getEngagementMetrics(apiParams);
      
      // Fetch social media performance
      const socialData = await analyticsService.getSocialMediaPerformance(apiParams);
      
      // Set all data
      set({
        dashboardData: dashboardData.data,
        contentAnalytics: contentData.data,
        socialMediaAnalytics: socialData.data,
        isLoading: false,
      });
      
      return {
        dashboard: dashboardData.data,
        content: contentData.data,
        engagement: engagementData.data,
        social: socialData.data,
      };
    } catch (error) {
      set({
        error: error.message || 'Failed to fetch dashboard data',
        isLoading: false,
      });
      console.error('Dashboard data error:', error);
      useNotificationStore.getState().showError(
        error.message || 'Failed to fetch dashboard data'
      );
      return null;
    }
  },

  // Fetch audience analytics
  fetchAudienceAnalytics: async (params = {}) => {
    set({ isLoading: true, error: null });
    try {
      const apiParams = get().prepareApiParams(params);
      console.log('Fetching audience analytics with params:', apiParams);
      
      const response = await analyticsService.getAudienceAnalytics(apiParams);
      set({
        audienceAnalytics: response.data,
        isLoading: false,
      });
      return response.data;
    } catch (error) {
      set({
        error: error.message || 'Failed to fetch audience analytics',
        isLoading: false,
      });
      console.error('Audience analytics error:', error);
      useNotificationStore.getState().showError(
        error.message || 'Failed to fetch audience analytics'
      );
      return null;
    }
  },
  
  // Fetch financial analytics
  fetchFinancialAnalytics: async (params = {}) => {
    set({ isLoading: true, error: null });
    try {
      const apiParams = get().prepareApiParams(params);
      console.log('Fetching financial analytics with params:', apiParams);
      
      const response = await analyticsService.getFinancialAnalytics(apiParams);
      set({
        financialAnalytics: response.data,
        isLoading: false,
      });
      return response.data;
    } catch (error) {
      set({
        error: error.message || 'Failed to fetch financial analytics',
        isLoading: false,
      });
      console.error('Financial analytics error:', error);
      useNotificationStore.getState().showError(
        error.message || 'Failed to fetch financial analytics'
      );
      return null;
    }
  },
  
  // Fetch user activity analytics
  fetchUserActivityAnalytics: async (params = {}) => {
    set({ isLoading: true, error: null });
    try {
      const apiParams = get().prepareApiParams(params);
      console.log('Fetching user activity analytics with params:', apiParams);
      
      const response = await analyticsService.getUserActivityAnalytics(apiParams);
      set({
        userActivityAnalytics: response.data,
        isLoading: false,
      });
      return response.data;
    } catch (error) {
      set({
        error: error.message || 'Failed to fetch user activity analytics',
        isLoading: false,
      });
      console.error('User activity analytics error:', error);
      useNotificationStore.getState().showError(
        error.message || 'Failed to fetch user activity analytics'
      );
      return null;
    }
  },
  
  // Fetch competitive analytics
  fetchCompetitiveAnalytics: async (params = {}) => {
    set({ isLoading: true, error: null });
    try {
      const apiParams = get().prepareApiParams(params);
      console.log('Fetching competitive analytics with params:', apiParams);
      
      const response = await analyticsService.getCompetitiveAnalysis(apiParams);
      set({
        competitiveAnalytics: response.data,
        isLoading: false,
      });
      return response.data;
    } catch (error) {
      set({
        error: error.message || 'Failed to fetch competitive analytics',
        isLoading: false,
      });
      console.error('Competitive analytics error:', error);
      useNotificationStore.getState().showError(
        error.message || 'Failed to fetch competitive analytics'
      );
      return null;
    }
  },
  
  // Fetch regional analytics
  fetchRegionalAnalytics: async (params = {}) => {
    set({ isLoading: true, error: null });
    try {
      const apiParams = get().prepareApiParams(params);
      console.log('Fetching regional analytics with params:', apiParams);
      
      const response = await analyticsService.getRegionalAnalytics(apiParams);
      set({
        regionalAnalytics: response.data,
        isLoading: false,
      });
      return response.data;
    } catch (error) {
      set({
        error: error.message || 'Failed to fetch regional analytics',
        isLoading: false,
      });
      console.error('Regional analytics error:', error);
      useNotificationStore.getState().showError(
        error.message || 'Failed to fetch regional analytics'
      );
      return null;
    }
  },

  // Fetch time tracking analytics
  fetchTimeTrackingAnalytics: async (params = {}) => {
    set({ isLoading: true, error: null });
    try {
      const apiParams = get().prepareApiParams(params);
      console.log('=== FRONTEND: Sending to backend ===');
      console.log('Params:', apiParams);
      console.log('userId:', apiParams.userId);
      console.log('projectId:', apiParams.projectId);
      console.log('clientId:', apiParams.clientId);
      
      const response = await analyticsService.getTimeTrackingAnalytics(apiParams);
      // The response structure is: { status: 'success', data: { analytics: {...} } }
      const analyticsData = response?.data?.analytics;
      set({
        timeTrackingAnalytics: analyticsData,
        isLoading: false,
      });
      return analyticsData;
    } catch (error) {
      set({
        error: error.message || 'Failed to fetch time tracking analytics',
        isLoading: false,
      });
      console.error('Time tracking analytics error:', error);
      useNotificationStore.getState().showError(
        error.message || 'Failed to fetch time tracking analytics'
      );
      return null;
    }
  },

  // Export time tracking analytics
  exportTimeTrackingAnalytics: async (params = {}) => {
    set({ isExporting: true, error: null });
    try {
      const apiParams = get().prepareApiParams(params);
      const response = await analyticsService.exportTimeTrackingAnalytics(apiParams);
      set({ isExporting: false });
      return response;
    } catch (error) {
      set({
        error: error.message || 'Failed to export time tracking analytics',
        isExporting: false,
      });
      console.error('Export time tracking analytics error:', error);
      useNotificationStore.getState().showError(
        error.message || 'Failed to export time tracking analytics'
      );
      return null;
    }
  },
}));

export default useAnalyticsStore;